import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Navbar from "./components/Layout/Navbar";
import Footer from "./components/Layout/Footer";
import SEOLinksFooter from "./components/SEOLinksFooter";
import Home from "./pages/Home";
import HomeV2 from "./pages/HomeV2";
import CruisesIndex from "./pages/CruisesIndex";
import SEOLanding from "./pages/SEOLanding";
import Packages from "./pages/Packages";
import Gallery from "./pages/Gallery";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Boat from "./pages/Boat";
import BYOCruise from "./pages/BYOCruise";
import VividCruise from "./pages/VividCruise";
import BookVividCruise from "./pages/BookVividCruise";
import VividBookingSuccess from "./pages/VividBookingSuccess";
import Availability from "./pages/Availability";
import { BlogIndex, BlogPost } from "./pages/Blog";
import NetworkingEventsSydney from "./pages/NetworkingEventsSydney";
import PrivateBoatHire from "./pages/PrivateBoatHire";
import NYECruise from "./pages/NYECruise";
import BookNYECruise from "./pages/BookNYECruise";
import NYEBookingSuccess from "./pages/NYEBookingSuccess";
import AustraliaDayCruise from "./pages/AustraliaDayCruise";
import BoxingDayCruise from "./pages/BoxingDayCruise";
import BookEvent from "./pages/BookEvent";
import EventBookingSuccess from "./pages/EventBookingSuccess";
import DepositSuccess from "./pages/DepositSuccess";
import EventCruises from "./pages/EventCruises";
import EventLanding from "./pages/EventLanding";
import CruiseSlugResolver from "./pages/CruiseSlugResolver";
import TicketedCruisesIndex from "./pages/TicketedCruisesIndex";
import CruisePricingMenus from "./pages/CruisePricingMenus";
import AdminPortal from "./pages/AdminPortal";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import FloatingAvailability from "./components/FloatingAvailability";
import CheckoutErrorBoundary from "./components/CheckoutErrorBoundary";

/* Reset scroll on route change. If the URL has a #hash, scroll the matching
   element into view AFTER React renders the new page (so cross-page hash
   links like /cruises/nye-cruises-sydney-harbour#enquiry actually land on
   the enquiry section instead of dumping at the top). */
function ScrollToTop() {
 const { pathname, hash } = useLocation();
 useEffect(() => {
  if (hash) {
   // Wait one paint so the destination element is in the DOM
   const id = hash.replace(/^#/, "");
   const tryScroll = (attempt = 0) => {
    const el = document.getElementById(id);
    if (el) {
     el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (attempt < 8) {
     // Element may not exist yet during route mount — retry briefly
     setTimeout(() => tryScroll(attempt + 1), 80);
    }
   };
   setTimeout(() => tryScroll(0), 0);
  } else {
   window.scrollTo({ top: 0, behavior: "instant" });
  }
 }, [pathname, hash]);
 return null;
}

function PublicChrome({ children }) {
 const { pathname } = useLocation();
 const isAdmin = pathname.startsWith("/admin-magic-portal");
 if (isAdmin) {
  return <>{children}</>;
 }
 return (
  <>
   <Navbar />
   {children}
   <SEOLinksFooter />
   <Footer />
   <FloatingAvailability />
  </>
 );
}

function App() {
 return (
 <div className="App">
 <BrowserRouter>
 <ScrollToTop />
 <PublicChrome>
 <Routes>
 <Route path="/" element={<HomeV2 />} />
 <Route path="/v1" element={<Home />} />
 <Route path="/cruises" element={<CruisesIndex />} />
 <Route path="/ticketed-cruises" element={<TicketedCruisesIndex />} />
 <Route path="/rates-and-menus" element={<CruisePricingMenus />} />
 <Route path="/cruise-menu-pricing" element={<CruisePricingMenus />} />
 <Route path="/event-cruises" element={<EventCruises />} />
 <Route path="/cruises/vivid-cruises-sydney" element={<VividCruise />} />
 <Route path="/cruises/vivid-cruises-sydney/book" element={<CheckoutErrorBoundary><BookVividCruise /></CheckoutErrorBoundary>} />
 <Route path="/cruises/vivid-cruises-sydney/booking/success" element={<VividBookingSuccess />} />
 <Route path="/cruises/private-boat-hire-sydney-harbour" element={<PrivateBoatHire />} />
 <Route path="/cruises/nye-cruises-sydney-harbour" element={<NYECruise />} />
 <Route path="/cruises/nye-cruises-sydney-harbour/book" element={<CheckoutErrorBoundary><BookNYECruise /></CheckoutErrorBoundary>} />
 <Route path="/cruises/nye-cruises-sydney-harbour/booking/success" element={<NYEBookingSuccess />} />
 <Route path="/cruises/australia-day-cruises-sydney-harbour" element={<AustraliaDayCruise />} />
 <Route path="/cruises/australia-day-cruises-sydney-harbour/book" element={<CheckoutErrorBoundary><BookEvent /></CheckoutErrorBoundary>} />
 <Route path="/cruises/australia-day-cruises-sydney-harbour/booking/success" element={<EventBookingSuccess slugDefault="australia-day-cruises-sydney-harbour" />} />
 <Route path="/cruises/boxing-day-cruises-sydney" element={<BoxingDayCruise />} />
 <Route path="/cruises/boxing-day-cruises-sydney/book" element={<CheckoutErrorBoundary><BookEvent /></CheckoutErrorBoundary>} />
 <Route path="/cruises/boxing-day-cruises-sydney/booking/success" element={<EventBookingSuccess slugDefault="boxing-day-cruises-sydney" />} />
 <Route path="/deposit/success" element={<DepositSuccess />} />
 <Route path="/deposit/cancel" element={<DepositSuccess />} />
 <Route path="/cruises/byo-cruise-sydney-harbour" element={<BYOCruise />} />
 <Route path="/cruises/wedding-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/birthday-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/corporate-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/hens-party-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/bucks-party-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/engagement-party-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/christmas-party-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/eofy-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/graduation-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/anniversary-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/cocktail-party-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/sunset-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/team-building-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/melbourne-cup-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/harbour-function-venue-sydney" element={<EventLanding />} />
 <Route path="/cruises/boat-party-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/family-celebration-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/celebration-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/engagement-proposal-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/private-harbour-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/product-launch-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/harbour-party-boat-hire-sydney" element={<EventLanding />} />
 <Route path="/cruises/romantic-dinner-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/networking-event-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/baby-shower-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/bridal-shower-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/catamaran-hire-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/harbour-dining-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/vip-harbour-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/valentines-day-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/halloween-cruises-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/sydney-harbour-charters" element={<EventLanding />} />
 <Route path="/cruises/21st-birthday-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/30th-birthday-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/40th-birthday-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/client-entertainment-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/business-networking-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/womens-networking-events-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/corporate-networking-cruises-sydney" element={<EventLanding />} />
 {/* Newly migrated SEO landing pages — served via EventLanding template (2,500+ words, no prices) */}
 <Route path="/cruises/startup-networking-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/leadership-event-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/women-in-business-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/executive-networking-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/entrepreneur-networking-events-sydney" element={<EventLanding />} />
 <Route path="/cruises/professional-networking-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/business-events-sydney-harbour" element={<EventLanding />} />
 <Route path="/cruises/sydney-harbour-event-cruises" element={<EventLanding />} />
 <Route path="/cruises/boat-hire-for-events-sydney" element={<EventLanding />} />
 <Route path="/cruises/school-formal-cruises-sydney" element={<EventLanding />} />
 <Route path="/cruises/:slug" element={<CruiseSlugResolver />} />
 <Route path="/packages" element={<Packages />} />
 <Route path="/boat" element={<Boat />} />
 <Route path="/byo-cruise-sydney" element={<Navigate to="/cruises/byo-cruise-sydney-harbour" replace />} />
 <Route path="/gallery" element={<Gallery />} />
 <Route path="/availability" element={<Availability />} />
 <Route path="/faq" element={<FAQ />} />
 <Route path="/contact" element={<Contact />} />
 <Route path="/networking-events-sydney" element={<NetworkingEventsSydney />} />
 <Route path="/blog" element={<BlogIndex />} />
 <Route path="/blog/:slug" element={<BlogPost />} />
 <Route path="/admin-magic-portal" element={<AdminPortal />} />
 <Route path="/privacy" element={<Privacy />} />
 <Route path="/terms" element={<Terms />} />
 <Route path="*" element={<Home />} />
 </Routes>
 </PublicChrome>
 <Toaster position="top-right" theme="dark" richColors />
 </BrowserRouter>
 </div>
 );
}

export default App;
